"use strict";

exports.__esModule = true;
exports.getCookie = exports.utmGrabber = exports.setMockReferrer = void 0;
var defaultNullValue = "[not provided]";
var firstKnownUtmDateCookieName = "first_known_utm_date"; // For testing purposes

var _mockReferrer;
/**
 * Helper function to set a mock referrer for testing purposes
 * @param referrer - The mock referrer URL to use
 */


function setMockReferrer(referrer) {
  _mockReferrer = referrer !== null && referrer !== void 0 ? referrer : undefined;
}

exports.setMockReferrer = setMockReferrer;
/**
 * Helper function to get the current referrer, using mock if set
 */

function getCurrentReferrer() {
  // Only return mock referrer if explicitly set
  if (_mockReferrer !== undefined) {
    return _mockReferrer;
  }

  return document.referrer;
}
/**
 * Helper function to check if traffic is direct (no referrer)
 * This should only return true for actual direct traffic, not mock referrers
 */


function isDirectTraffic() {
  // If mock referrer is set, it's not direct traffic
  if (_mockReferrer !== undefined) {
    return false;
  } // Check document.referrer directly for direct traffic


  return !document.referrer || document.referrer === "";
}
/**
 * Helper function to check if we're in a browser environment
 */


function isBrowser() {
  return typeof window !== "undefined";
}
/**
 * Helper function to check if we should use secure cookies
 */


function shouldUseSecureCookies() {
  if (!isBrowser()) return true; // Default to secure in non-browser environments

  return window.location.protocol === "https:";
} // Configuration


var CONFIG = {
  COOKIE_SETTINGS: {
    sameSite: "lax",
    path: "/"
  },
  EXPIRE_TIMES: {
    regular: 60 * 60 * 24 * 30,
    firstKnown: 34560000
  }
}; // Configuration for internal properties

var INTERNAL_PROPERTIES = {
  BLOG: {
    path: "/blog",
    referrer: "blog"
  },
  DOCS: {
    path: "/docs",
    referrer: "docs"
  },
  TUTORIALS: {
    path: "/tutorials",
    referrer: "tutorials"
  },
  WWW_PRICING: {
    path: "/pricing",
    referrer: "www_pricing"
  },
  WWW_SOLUTIONS: {
    path: "/solutions",
    referrer: "www_solutions"
  },
  WWW: {
    path: "",
    referrer: "www"
  },
  SANDBOX: {
    referrer: "embeddedsandbox"
  }
};
/**
 * Main function to start the UTM capturing process.
 * It does four things:
 *   1. Sets the session_referrer (and session_referrer_created_at) cookie when a new session starts (using rolling expiry).
 *   2. Processes the visitor's document referrer and sets related UTM cookies.
 *   3. Extracts UTM parameters from the URL and stores them as cookies.
 *   4. Records the date when UTM info was first captured.
 * @param isSignupFlow - Whether this is being called from the signup flow
 */

function utmGrabber(isSignupFlow) {
  if (isSignupFlow === void 0) {
    isSignupFlow = false;
  }

  handleSessionReferrerCookies(); // Handles session-based tracking and sets session_referrer, session_referrer_details and session_referrer_created_at cookies

  if (!isSignupFlow) handleLastPageCookie();
  handleReferrerCookies(); // Generates referrer and referrer_details cookies, not scoped to the active session (these are 'last touch')

  handleDocumentReferrer(); // Handles setting UTM campaign cookies based on the document.referrer, organic search traffic is handled here

  handleUTMGrabbing(); // Looks for UTM campaign parameters in the URL and sets cookies

  handleSetFirstKnownUtmDate(); // Records the first visit date when UTM info was captured.
}

exports.utmGrabber = utmGrabber;
/** Helper function for tracking last page as user navigates site */

/**
 * This function only runs on non-studio routes in order
 * to allow us to set referrer_details for things like embedded sandbox
 */

function handleLastPageCookie() {
  var currentURL = window.location.href.split("?")[0] || "";
  var lastPage = getCookie("last_page");

  if (decodeURIComponent(lastPage || "") !== currentURL && !window.location.hostname.includes("studio.")) {
    setSessionCookie("last_page", currentURL);
  }
}
/**
 * Helper function to check if a URL is a signup/login related page
 */


function isSignupRelatedPage(url) {
  try {
    var urlObj_1 = new URL(url);
    var signupPaths = ["/signup", "/welcome", "/login"];
    var isSignupPath = signupPaths.some(function (path) {
      return urlObj_1.pathname.startsWith(path);
    });
    return isSignupPath;
  } catch (error) {
    console.error("Error checking signup page:", error);
    return false;
  }
}
/**
 * Determines if a referrer URL is from an internal domain that should be tracked
 * We want to track transitions between internal apps, but ignore internal navigation
 * within the same app
 */


function isTrackableReferral(referrer) {
  try {
    if (!referrer) return false;

    if (referrer === "direct" || !referrer.includes("://")) {
      return false;
    }

    var currentUrl = window.location.href;
    var referrerUrl = new URL(referrer);
    var currentUrlObj = new URL(currentUrl);

    if (isSignupRelatedPage(currentUrl)) {
      return true;
    }

    if (isSignupRelatedPage(referrer)) {
      return false;
    }

    var isSamePage = referrerUrl.pathname === currentUrlObj.pathname && referrerUrl.hostname === currentUrlObj.hostname && referrerUrl.port === currentUrlObj.port;

    if (isSamePage) {
      return false;
    } // Check if referrer is internal


    var isReferrerInternal = referrerUrl.hostname.endsWith(getRootDomain()); // If referrer is internal, it's not trackable (it's internal navigation)

    return !isReferrerInternal;
  } catch (error) {
    console.error("Error checking trackable referral:", error);
    return false;
  }
}
/**
 * Determines the internal property from a URL
 * Returns the property identifier (blog, docs, tutorials, www) or null if not matched
 */


function getInternalProperty(url) {
  try {
    if (!url) return null;
    var urlObj = new URL(url);
    var pathname = urlObj.pathname; // Check each internal property's path

    for (var _i = 0, _a = Object.values(INTERNAL_PROPERTIES); _i < _a.length; _i++) {
      var property = _a[_i];

      if ("path" in property && pathname.startsWith(property.path)) {
        return property.referrer;
      }
    } // If no path matches, return www


    return INTERNAL_PROPERTIES.WWW.referrer;
  } catch (error) {
    console.error("Error determining internal property:", error);
    return null;
  }
}
/**
 * Gets a clean site name from a URL for earned traffic
 * Example: https://dev.to/article -> dev.to
 */


function getEarnedSiteName(url) {
  try {
    var urlObj = new URL(url); // Remove www. prefix if it exists

    return urlObj.hostname.replace(/^www\./, "");
  } catch (error) {
    console.error("Error getting earned site name:", error);
    return "unknown";
  }
}
/**
 * Helper function to get URL parameters from the current window location
 */


function getUrlParams() {
  return new URLSearchParams(window.location.search);
}
/**
 * Determines referrer values for paid search traffic
 */


function getPaidSearchReferrerValues(utmCampaign) {
  return {
    referrer: "paidsearch",
    referrerDetails: utmCampaign || "unknown"
  };
}
/**
 * Determines referrer values for search engine traffic
 */


function getSearchEngineReferrerValues(searchEngine) {
  if (isPaidSearch(searchEngine)) {
    return {
      referrer: "paidsearch_".concat(searchEngine.source),
      referrerDetails: cleanUrl(window.location.href)
    };
  }

  return {
    referrer: "organicsearch_".concat(searchEngine.source),
    referrerDetails: cleanUrl(window.location.href)
  };
}
/**
 * Determines referrer values for external site traffic
 */


function getExternalSiteReferrerValues(cleanDocumentReferrer) {
  var earnedSiteName = getEarnedSiteName(cleanDocumentReferrer);
  return {
    referrer: "earned_".concat(earnedSiteName),
    referrerDetails: cleanDocumentReferrer
  };
}
/**
 * Determines referrer values for internal navigation
 */


function getInternalNavigationReferrerValues(lastPage) {
  var internalProperty = getInternalProperty(lastPage);

  if (internalProperty) {
    return {
      referrer: internalProperty,
      referrerDetails: lastPage
    };
  }

  return null;
}
/**
 * Utility function to clean URLs by removing query parameters
 */


function cleanUrl(url) {
  try {
    var urlObj = new URL(url);
    return "".concat(urlObj.protocol, "//").concat(urlObj.host).concat(urlObj.pathname);
  } catch (_a) {
    return url;
  }
}
/**
 * Helper for matching search engines
 */


function findSearchEngine(url) {
  try {
    var urlObj = new URL(url);
    var hostname_1 = urlObj.hostname.toLowerCase();
    return searchEngines.find(function (engine) {
      var engineDomain = engine.name.toLowerCase();
      return hostname_1.includes(engineDomain);
    });
  } catch (_a) {
    return undefined;
  }
}
/**
 * Helper for handling internal navigation
 */


function handleInternalNavigation(cleanReferrer, lastPage) {
  if (!lastPage) return null;
  var isInternal = cleanReferrer.includes(getRootDomain());
  if (!isInternal) return null; // handle sandbox embeds

  if (cleanReferrer.includes("sandbox.embed")) {
    return {
      referrer: INTERNAL_PROPERTIES.SANDBOX.referrer,
      referrerDetails: lastPage
    };
  }

  return getInternalNavigationReferrerValues(lastPage);
}
/**
 * Helper for setting UTM cookies
 */


function setUtmCookies(source, medium, otherParams) {
  if (otherParams === void 0) {
    otherParams = {};
  }

  setCookie("utm_source", source, CONFIG.EXPIRE_TIMES.regular);
  setCookie("utm_medium", medium, CONFIG.EXPIRE_TIMES.regular); // Set other UTM parameters

  Object.entries(otherParams).forEach(function (_a) {
    var param = _a[0],
        value = _a[1];
    setCookie(param, value, CONFIG.EXPIRE_TIMES.regular);
  }); // Set default values for missing parameters

  utmParams.forEach(function (param) {
    if (!otherParams[param] && param !== "utm_source" && param !== "utm_medium") {
      setCookie(param, defaultNullValue, CONFIG.EXPIRE_TIMES.regular);
    }
  });
}
/**
 * Helper for setting direct traffic UTM cookies
 */


function handleDirectTraffic() {
  var firstKnownUtmDate = getCookie(firstKnownUtmDateCookieName); // Only set UTM cookies for direct traffic if they don't already exist

  var existingUtmSource = getCookie("utm_source");
  var existingUtmMedium = getCookie("utm_medium");

  if (!existingUtmSource || existingUtmSource === defaultNullValue) {
    setCookie("utm_source", "direct", CONFIG.EXPIRE_TIMES.regular);
  }

  if (!existingUtmMedium || existingUtmMedium === defaultNullValue) {
    setCookie("utm_medium", "website", CONFIG.EXPIRE_TIMES.regular);
  }

  if (!firstKnownUtmDate) {
    setCookie("first_known_utm_source", "direct", CONFIG.EXPIRE_TIMES.firstKnown);
    setCookie("first_known_utm_medium", "website", CONFIG.EXPIRE_TIMES.firstKnown);
  }
}
/**
 * Determines the referrer and referrer_details values based on the following priority:
 * 1. Referrer Parameters in URL
 * 2. UTM Parameters
 * 3. External Referrers (Search Engines and External Sites) - unless in signup flow from internal source
 * 4. Internal Navigation
 * 5. Direct Traffic
 */


function handleReferrerValues() {
  var _a, _b;

  var urlParams = getUrlParams();
  var documentReferrer = getCurrentReferrer();
  var cleanReferrer = cleanUrl(documentReferrer);
  var lastPage = getCookie("last_page"); // Check for referrer param in URL first, this always takes precedence

  var referrerUrlParam = urlParams.get("referrer");

  if (referrerUrlParam) {
    var referrerDetails = urlParams.get("referrer_details"); // If no referrer_details param and it's an internal referral, use lastPage cookie

    if (!referrerDetails && cleanReferrer && cleanReferrer.includes(getRootDomain())) {
      referrerDetails = lastPage;
    } // Fallback to existing logic


    if (!referrerDetails) {
      referrerDetails = cleanReferrer || cleanUrl(window.location.href) || "";
    }

    return {
      referrer: referrerUrlParam,
      referrerDetails: referrerDetails
    };
  }

  var utmSource = urlParams.get("utm_source");
  var utmMedium = urlParams.get("utm_medium");
  var utmCampaign = urlParams.get("utm_campaign");
  var gclid = urlParams.get("gclid"); // Check for paid ads traffic

  if (utmMedium === "cpc" || gclid) {
    return getPaidSearchReferrerValues(utmCampaign);
  } // Check for other UTM parameters


  if (utmSource && utmMedium) {
    return {
      referrer: "".concat(utmMedium, "_").concat(utmSource),
      referrerDetails: utmCampaign || cleanUrl(window.location.href)
    };
  } // Check internal navigation


  var internalValues = handleInternalNavigation(cleanReferrer, lastPage);

  if (internalValues) {
    return internalValues;
  } // Check external referrers & handle search engine referrals


  if (cleanReferrer && isTrackableReferral(cleanReferrer)) {
    var searchEngine = findSearchEngine(cleanReferrer);

    if (searchEngine) {
      return getSearchEngineReferrerValues(searchEngine);
    }

    return getExternalSiteReferrerValues(cleanReferrer);
  } // Handle direct traffic


  if (!cleanReferrer) {
    return {
      referrer: "direct",
      referrerDetails: cleanUrl(window.location.href)
    };
  } // Return existing values


  return {
    referrer: (_a = getCookie("referrer")) !== null && _a !== void 0 ? _a : "",
    referrerDetails: (_b = getCookie("referrer_details")) !== null && _b !== void 0 ? _b : ""
  };
}
/**
 * List of UTM parameters we care about.
 */


var utmParams = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_icid", "utm_ICID", "gclid"];
/**
 * List of recognized search engines with their organic and paid search parameters
 */

var searchEngines = [// Google (including all TLDs like .com, .ca, .co.uk, etc.)
{
  name: "google.",
  url: "google.com",
  source: "google",
  paidParam: "gclid"
}, // Bing
{
  name: "bing",
  url: "bing.com",
  source: "bing",
  paidParam: "msclkid"
}, // Yahoo
{
  name: "yahoo",
  url: "yahoo.com",
  source: "yahoo"
}, // DuckDuckGo
{
  name: "duckduckgo",
  url: "duckduckgo.com",
  source: "duckduckgo"
}, // Baidu
{
  name: "baidu",
  url: "baidu.com",
  source: "baidu"
}, // Yandex
{
  name: "yandex",
  url: "yandex.",
  source: "yandex"
}, // Ecosia
{
  name: "ecosia",
  url: "ecosia.org",
  source: "ecosia"
}, // Qwant
{
  name: "qwant",
  url: "qwant.com",
  source: "qwant"
}, // StartPage
{
  name: "startpage",
  url: "startpage.com",
  source: "startpage"
}, // Brave Search
{
  name: "search.brave",
  url: "search.brave.com",
  source: "brave"
}];
/**
 * Helper function to check if traffic is from paid search
 */

function isPaidSearch(searchEngine) {
  if (!searchEngine.paidParam) return false;
  var urlParams = new URLSearchParams(window.location.search);
  return urlParams.has(searchEngine.paidParam) || urlParams.get("utm_medium") === "cpc";
}
/**
 * Class to handle cookie operations with consistent settings
 */


var CookieManager =
/** @class */
function () {
  function CookieManager() {
    this.config = {
      sameSite: "lax",
      path: "/",
      domain: isBrowser() ? getRootDomain() : "",
      secure: shouldUseSecureCookies()
    };
  }

  CookieManager.prototype.set = function (name, value, maxAgeSeconds) {
    try {
      var cookieValue = "".concat(name, "=").concat(encodeURIComponent(value), "; samesite=").concat(this.config.sameSite, "; path=").concat(this.config.path, "; ").concat(this.config.domain ? "domain=".concat(this.config.domain, ";") : "", " max-age=").concat(maxAgeSeconds).concat(this.config.secure ? "; secure" : "");
      document.cookie = cookieValue;
    } catch (error) {
      console.error("Error setting cookie ".concat(name, ":"), error);
    }
  };

  CookieManager.prototype.setSession = function (name, value) {
    try {
      var cookieValue = "".concat(name, "=").concat(encodeURIComponent(value), "; samesite=lax; path=/; ").concat(this.config.domain ? "domain=".concat(this.config.domain, ";") : "");
      document.cookie = cookieValue;
    } catch (error) {
      console.error("Error setting session cookie ".concat(name, ":"), error);
    }
  };

  CookieManager.prototype.get = function (name) {
    var _a;

    try {
      var nameEQ = "".concat(name, "=");
      var ca = document.cookie.split(";");

      for (var i = 0; i < ca.length; i++) {
        var c = (_a = ca[i]) === null || _a === void 0 ? void 0 : _a.trim();

        if ((c === null || c === void 0 ? void 0 : c.indexOf(nameEQ)) === 0) {
          return decodeURIComponent(c.substring(nameEQ.length));
        }
      }

      return null;
    } catch (error) {
      console.error("Error getting cookie ".concat(name, ":"), error);
      return null;
    }
  };

  CookieManager.prototype["delete"] = function (name) {
    document.cookie = "".concat(name, "=; path=/; samesite=lax; ").concat(this.config.domain ? "domain=".concat(this.config.domain, ";") : "", " expires=Thu, 01 Jan 1970 00:00:01 GMT;");
  };

  return CookieManager;
}();

var cookieManager = new CookieManager(); // Update existing cookie functions to use the manager

function setCookie(name, value, maxAgeSeconds) {
  cookieManager.set(name, value, maxAgeSeconds);
}

function setSessionCookie(name, value) {
  cookieManager.setSession(name, value);
}

function getCookie(name) {
  return cookieManager.get(name);
}

exports.getCookie = getCookie;

function deleteCookie(name) {
  cookieManager["delete"](name);
}
/**
 * Handles the session_referrer using rolling session expiry.
 */


function handleSessionReferrerCookies() {
  try {
    var date_1 = new Date();

    var _a = handleReferrerValues(),
        referrer_1 = _a.referrer,
        referrerDetails_1 = _a.referrerDetails;

    var sessionCookies = ["session_referrer", "session_referrer_details", "session_referrer_created_at"];
    var activeSessionExists = sessionCookies.some(function (cookieName) {
      return getCookie(cookieName);
    });

    if (activeSessionExists) {
      return;
    } // Always set session cookies regardless of referrer type


    sessionCookies.forEach(function (cookieName, index) {
      var value = index === 0 ? referrer_1 : index === 1 ? referrerDetails_1 : date_1.toISOString();
      setSessionCookie(cookieName, value);
    });
  } catch (error) {
    console.error("Error handling session referrer cookies:", error);
  }
}
/**
 * Handles setting the referrer and referrer_details cookies.
 * These cookies track the last touch before signup and use a longer expiration.
 */


function handleReferrerCookies() {
  var _a = handleReferrerValues(),
      referrer = _a.referrer,
      referrerDetails = _a.referrerDetails; // Always set referrer cookies regardless of page or referrer type


  setCookie("referrer", referrer, CONFIG.EXPIRE_TIMES.regular);
  setCookie("referrer_details", referrerDetails, CONFIG.EXPIRE_TIMES.regular);
}

function getSiteNameFromReferrer(referrer) {
  if (!referrer) {
    return "unknown";
  }

  try {
    var url = new URL(referrer);
    var hostname = url.hostname; // handle ip address cases

    if (hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
      return hostname;
    } // Remove www. prefix if it exists


    var cleanHostname = hostname.replace(/^www\./, "");
    var hostnameParts = cleanHostname.split(".");

    if (hostnameParts.length > 1) {
      hostnameParts.pop(); // Remove the last part (e.g., "com")
    }

    return hostnameParts.join("."); // Join the remaining parts to get the site name
  } catch (error) {
    console.error("Error parsing referrer URL:", error);
    return "unknown";
  }
}
/**
 * Helper function to check if there are any UTM parameters in the URL
 */


function hasUtmParamsInUrl() {
  var urlParams = getUrlParams();
  return utmParams.some(function (param) {
    return urlParams.has(param);
  });
}
/**
 * Checks if any UTM cookies exist
 */


function hasExistingUtmCookies() {
  return utmParams.some(function (param) {
    return getCookie(param) && getCookie(param) !== defaultNullValue;
  });
}
/**
 * Processes the document referrer and sets related UTM cookies.
 */


function handleDocumentReferrer() {
  try {
    var referrer = getCurrentReferrer();
    var cleanReferrer = cleanUrl(referrer);
    var siteName = getSiteNameFromReferrer(cleanReferrer); // Handle direct traffic - but only for actual direct navigation

    if (isDirectTraffic()) {
      handleDirectTraffic();
      return;
    } // Handle undefined referrer edge case


    if (cleanReferrer === "undefined") {
      handleDirectTraffic();
      return;
    } // Ignore non-trackable referrers


    if (!isTrackableReferral(cleanReferrer)) {
      return;
    }

    var searchEngine = findSearchEngine(cleanReferrer);
    var firstKnownUtmDate = getCookie(firstKnownUtmDateCookieName); // Only set UTM cookies from referrer if there are no UTM params in URL AND no existing UTM cookies

    if (!hasUtmParamsInUrl() && !hasExistingUtmCookies()) {
      // Clear existing UTM cookies only if we're going to set new ones
      utmParams.forEach(function (param) {
        if (getCookie(param)) {
          deleteCookie(param);
        }
      });

      if (searchEngine) {
        handleSearchEngineTraffic(searchEngine, firstKnownUtmDate);
      } else {
        handleReferralTraffic(siteName, firstKnownUtmDate);
      }
    }
  } catch (error) {
    console.error("Error handling document referrer:", error);
  }
}
/**
 * Handles setting UTM parameters for search engine traffic
 */


function handleSearchEngineTraffic(searchEngine, firstKnownUtmDate) {
  setUtmCookies(searchEngine.source, "organic");

  if (!firstKnownUtmDate) {
    setCookie("first_known_utm_medium", "organic", CONFIG.EXPIRE_TIMES.firstKnown);
    setCookie("first_known_utm_source", searchEngine.source, CONFIG.EXPIRE_TIMES.firstKnown);
  }
}
/**
 * Handles setting UTM parameters for referral traffic
 */


function handleReferralTraffic(siteName, firstKnownUtmDate) {
  setUtmCookies(siteName, "referral");

  if (!firstKnownUtmDate) {
    setCookie("first_known_utm_medium", "referral", CONFIG.EXPIRE_TIMES.firstKnown);
    setCookie("first_known_utm_source", siteName, CONFIG.EXPIRE_TIMES.firstKnown);
  }
}
/**
 * Extracts UTM parameters from the URL and stores them as cookies.
 */


function handleUTMGrabbing() {
  try {
    var urlParams_1 = getUrlParams();
    var utmsExist_1 = false;
    var firstKnownUtmDate_1 = getCookie(firstKnownUtmDateCookieName); // Check for any UTM parameters in the URL

    var presentUtmParams_1 = utmParams.reduce(function (acc, param) {
      var value = urlParams_1.get(param);

      if (value) {
        acc[param] = value;
        utmsExist_1 = true;
      }

      return acc;
    }, {});

    if (!utmsExist_1) {
      // If no UTM params in URL and no existing UTM cookies, set default values
      if (!hasExistingUtmCookies()) {
        utmParams.forEach(function (param) {
          setCookie(param, defaultNullValue, CONFIG.EXPIRE_TIMES.regular);
        });
      }

      return;
    } // Set cookies for present UTM parameters


    Object.entries(presentUtmParams_1).forEach(function (_a) {
      var param = _a[0],
          value = _a[1];
      deleteCookie(param);
      setCookie(param, value, CONFIG.EXPIRE_TIMES.regular);

      if (!firstKnownUtmDate_1) {
        setCookie("first_known_".concat(param), value, CONFIG.EXPIRE_TIMES.firstKnown);
      }
    }); // Set default values for missing UTM parameters

    utmParams.forEach(function (param) {
      if (!presentUtmParams_1[param]) {
        deleteCookie(param);
        setCookie(param, defaultNullValue, CONFIG.EXPIRE_TIMES.regular);
      }
    });
  } catch (error) {
    console.error("Error handling UTM parameters:", error);
  }
}
/**
 * Records the date and time of the first visit where UTM information was captured.
 */


function handleSetFirstKnownUtmDate() {
  if (!getCookie(firstKnownUtmDateCookieName)) {
    setCookie(firstKnownUtmDateCookieName, new Date().toISOString(), CONFIG.EXPIRE_TIMES.firstKnown);
  }
}
/**
 * Helper function to get the root domain from a hostname
 */


function getRootDomain() {
  if (!isBrowser()) return "";
  var hostname = window.location.hostname;
  var parts = hostname.split("."); // Handle localhost and IP addresses

  if (parts.length <= 1 || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return hostname;
  } // Handle Netlify preview domains (e.g., deploy-preview-12137--apollo-studio.netlify.app)
  // These should use the full hostname to avoid cross-domain cookie issues


  if (hostname.includes("--") && hostname.endsWith(".netlify.app")) {
    return hostname;
  } // For domains like www.apollographql.com, return apollographql.com


  return parts.slice(-2).join(".");
}